import { useState, useCallback, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { useNotification } from "hooks/useNotification";
import { Facility } from "types/facility.type";
import {
  Separator,
  isAvskiljare,
  isWaterMeasurementPoint,
} from "types/separator/separator.type";
import { createUppdragV2, UppdragQueryKey } from "api/uppdrag.api";
import { TransportSedelQueryKey } from "api/transportSedel.api";
import {
  Uppdrag,
  UppdragScheduleDateType,
  UppdragStatus,
} from "types/uppdrag.type";
import { useNavigate } from "@tanstack/react-router";
import { UppdragPageTab } from "pages/app/uppdrag/UppdragPage";
import { getSeparatorTypeLabel } from "types/separator/separator.helpers";
import { useWasteCodeDropDown, getMostUsedWasteCodes } from "api/wasteCode.api";
import { ComboBoxItem } from "components/ComboBox";
import { useCurrentCompany } from "api/company.api";
import { useWasteStationList } from "api/wasteStation.api";
import { Role, TomningType } from "types/enums";
import { useUsers } from "api/user.api";
import { startOfMonth, startOfWeek } from "date-fns";
import userStore, { getUser, getUserRole } from "store/user.store";
import { WasteCode } from "types/wasteCode.type";
import { useIsFeatureFlagEnabled } from "hooks/useFeatureFlag";
import { FeatureFlagName } from "api/featureFlag.api";

const formSchema = z
  .object({
    // Customer and facility selection (both provided)
    customerUuid: z.string().uuid("Välj en kund"),
    facilityUuid: z.string().uuid("Välj en anläggning"),
    separatorUuid: z
      .string()
      .uuid("Välj ett objekt")
      .optional()
      .or(z.literal(""))
      .nullable(),

    // Schedule and assignment fields (not required for operators)
    assignedToUserUuid: z.string().uuid("Välj en operatör").optional(),
    scheduleDateType: z.nativeEnum(UppdragScheduleDateType).optional(),
    scheduleDate: z.date().optional(),
    description: z.string().optional(),
    referenceNumber: z.string().optional(),
    isPrioritized: z.boolean().default(false),

    // Task selection (not required)
    uppgifter: z.object({
      tomning: z.boolean().default(false),
      sixMonths: z.boolean().default(false),
      fiveYears: z.boolean().default(false),
      waterMeasurement: z.boolean().default(false),
    }),

    // Waste transport fields
    wasteCodeUuid: z.string().uuid().optional(),
    wasteCodeQuery: z.string().nullable().optional(),
    selectedWasteCode: z.custom<ComboBoxItem>().optional(),
    wasteStationUuid: z.string().uuid().optional(),
    wasteStationDeliveryDate: z.date().optional().nullable(),
    preliminaryWeight: z
      .string()
      .optional()
      .refine((val) => !val || !isNaN(Number(val)), {
        message: "Måste vara ett nummer",
      }),
    estimatedWorkingHours: z.string().optional(),
    estimatedDrivingHours: z.string().optional(),
    tomningType: z.nativeEnum(TomningType).optional(),
  })
  .superRefine((data, ctx) => {
    const userRole = userStore.getState().user?.role;
    const isOperator = userRole === Role.Operator;

    // Separator is only required if sixMonths, fiveYears or waterMeasurement are selected
    // Tomning can be done without a separator
    if (
      (data.uppgifter.sixMonths ||
        data.uppgifter.fiveYears ||
        data.uppgifter.waterMeasurement) &&
      !data.separatorUuid
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Välj ett objekt för den valda aktiviteten",
        path: ["separatorUuid"],
      });
    }

    // For Admin users, assignedToUserUuid and schedule fields are required
    if (!isOperator) {
      if (!data.assignedToUserUuid) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Välj en operatör",
          path: ["assignedToUserUuid"],
        });
      }
      if (!data.scheduleDateType) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Välj datumprecision",
          path: ["scheduleDateType"],
        });
      }
      if (!data.scheduleDate) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Välj datum",
          path: ["scheduleDate"],
        });
      }
    }

    // If tomning is selected, wasteCodeUuid is required
    if (data.uppgifter.tomning) {
      if (!data.wasteCodeUuid) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Avfallskod är obligatorisk för avfallstransport",
          path: ["wasteCodeUuid"],
        });
      }
      if (!data.wasteStationUuid) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Mottagarstation är obligatorisk för avfallstransport",
          path: ["wasteStationUuid"],
        });
      }
    }

    // No validation required for uppgifter - it's allowed to create uppdrag without any activities
  });

type FormData = z.infer<typeof formSchema>;

export const useAddUppdragFromFacilityPage = (facility: Facility) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const notification = useNotification();
  const isWasteStationNotificationEnabled = useIsFeatureFlagEnabled(
    FeatureFlagName.IsWasteStationNotificationEnabled,
  );
  const user = userStore(getUser);
  const userRole = userStore(getUserRole);
  const isOperator = userRole === Role.Operator;
  const { data: company } = useCurrentCompany();
  const { data: users } = useUsers({}, { roles: [Role.Operator, Role.Admin] });
  const [isModalOpen, setIsModalOpen] = useState(false);

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    mode: "onChange",
    defaultValues: {
      customerUuid: facility.customer.uuid,
      facilityUuid: facility.uuid,
      separatorUuid: "",
      assignedToUserUuid: isOperator ? user?.uuid : "",
      scheduleDateType: UppdragScheduleDateType.Day,
      scheduleDate: new Date(),
      isPrioritized: false,
      description: "",
      referenceNumber: "",
      uppgifter: {
        tomning: false,
        sixMonths: false,
        fiveYears: false,
        waterMeasurement: false,
      },
      selectedWasteCode: {
        id: "",
        name: "",
        description: "",
      },
      wasteCodeQuery: "",
      wasteStationDeliveryDate: new Date(),
      preliminaryWeight: "",
      estimatedWorkingHours: "",
      estimatedDrivingHours: "",
      tomningType: TomningType.Heltomning,
    },
  });

  const openModal = () => {
    form.setValue("customerUuid", facility.customer.uuid);
    form.setValue("facilityUuid", facility.uuid);
    setIsModalOpen(true);
  };

  const closeModal = () => setIsModalOpen(false);

  const afterModalClose = () => {
    form.reset({
      ...form.formState.defaultValues,
      customerUuid: facility.customer.uuid,
      facilityUuid: facility.uuid,
    });
  };

  const facilitySeparatorList = useCallback((): Separator[] => {
    return facility.separatorList || [];
  }, [facility]);

  const getSeparatorDropdownItems = () => {
    const separators = facilitySeparatorList();
    return separators.map((separator) => ({
      label: `${separator.identifier} - ${getSeparatorTypeLabel(separator)}`,
      value: separator.uuid,
    }));
  };

  // User dropdown items (for admin only)
  const userDropdownItems = useMemo(
    () =>
      !company
        ? []
        : (users ?? [])
            .filter(
              (u) =>
                u.role === Role.Operator ||
                company.settings.isUppdragSchedulingToAdminActivated,
            )
            .map((x) => ({ label: x.name, value: x.uuid }))
            .sort((a, b) => (a.label < b.label ? -1 : 1)),
    [users, company],
  );

  // Auto-select single separator when modal opens
  useEffect(() => {
    if (isModalOpen) {
      const separators = facilitySeparatorList();
      if (separators.length === 1) {
        form.setValue("separatorUuid", separators[0].uuid);
      } else if (separators.length > 1 && isOperator) {
        // For operators, auto-select first separator
        form.setValue("separatorUuid", separators[0].uuid);
      }
    }
  }, [isModalOpen, form, facilitySeparatorList, isOperator]);

  // Clear inspection uppgifter if separator is cleared or changed to an incompatible type
  useEffect(() => {
    const separatorUuid = form.watch("separatorUuid");
    const tomning = form.watch("uppgifter.tomning");
    const sixMonths = form.watch("uppgifter.sixMonths");
    const fiveYears = form.watch("uppgifter.fiveYears");
    const waterMeasurement = form.watch("uppgifter.waterMeasurement");

    if (!tomning && !sixMonths && !fiveYears && !waterMeasurement) {
      // No uppgifter selected, nothing to clear
      return;
    }

    // If separator is cleared
    if (!separatorUuid) {
      if (sixMonths) form.setValue("uppgifter.sixMonths", false);
      if (fiveYears) form.setValue("uppgifter.fiveYears", false);
      if (waterMeasurement) form.setValue("uppgifter.waterMeasurement", false);
      return;
    }

    const separators = facilitySeparatorList();
    const selectedSeparator = separators.find((s) => s.uuid === separatorUuid);
    if (!selectedSeparator) {
      return;
    }

    // If separator changed to non-avskiljare
    if (!isAvskiljare(selectedSeparator)) {
      if (sixMonths) form.setValue("uppgifter.sixMonths", false);
      if (fiveYears) form.setValue("uppgifter.fiveYears", false);
    }

    // Water measurement is only allowed on a WaterMeasurementPoint,
    // and a WaterMeasurementPoint only allows water measurement
    if (isWaterMeasurementPoint(selectedSeparator)) {
      if (tomning) form.setValue("uppgifter.tomning", false);
    } else if (waterMeasurement) {
      form.setValue("uppgifter.waterMeasurement", false);
    }
  }, [form.watch("separatorUuid"), form, facilitySeparatorList]);

  // Update schedule date based on schedule date type (Admin only)
  useEffect(() => {
    if (isOperator) return;

    const subscription = form.watch((value, { name }) => {
      if (name === "scheduleDateType" && value.scheduleDate) {
        const scheduleDate = value.scheduleDate;
        let newDate = scheduleDate;

        switch (value.scheduleDateType) {
          case UppdragScheduleDateType.Week:
            newDate = startOfWeek(scheduleDate, { weekStartsOn: 1 });
            break;
          case UppdragScheduleDateType.Month:
            newDate = startOfMonth(scheduleDate);
            break;
        }

        if (newDate !== scheduleDate) {
          form.setValue("scheduleDate", newDate);
        }
      }
    });
    return () => subscription.unsubscribe();
  }, [form, isOperator]);

  // Waste code logic
  const wasteCodeQuery = form.watch("wasteCodeQuery") || "";
  const selectedWasteCode = form.watch("selectedWasteCode");

  const { data: wasteCodes } = useWasteCodeDropDown(wasteCodeQuery);
  const { data: mostUsedWasteCodes } = useQuery(getMostUsedWasteCodes(3));

  // Waste station logic
  const { data: wasteStations } = useWasteStationList();
  const wasteStationDropdownItems = useMemo(
    () =>
      (wasteStations ?? [])
        .map((x) => ({ label: x.name, value: x.uuid }))
        .sort((a, b) => (a.label < b.label ? -1 : 1)),
    [wasteStations],
  );

  const wasteCodeComboBoxItems: ComboBoxItem[] = (wasteCodes ?? []).map(
    (c) => ({
      id: c.uuid,
      name: c.codePretty,
      description: c.description,
    }),
  );

  const setWasteCodeQuery = useCallback(
    (v?: string) => {
      form.setValue("wasteCodeQuery", v || "");
    },
    [form],
  );

  const setSelectedWasteCode = useCallback(
    (wc: ComboBoxItem) => {
      form.setValue(
        "selectedWasteCode",
        wc ?? {
          id: "",
          name: "",
          description: "",
        },
      );
      form.setValue("wasteCodeUuid", wc?.id ?? undefined);
    },
    [form],
  );

  const selectMostUsedWasteCode = useCallback(
    (wasteCode: WasteCode) => {
      const wasteCodeItem: ComboBoxItem = {
        id: wasteCode.uuid,
        name: wasteCode.codePretty,
        description: wasteCode.description,
      };
      setSelectedWasteCode(wasteCodeItem);
    },
    [setSelectedWasteCode],
  );

  // Create uppdrag mutation - single endpoint with role-based success handling
  const createUppdragMutation = useMutation({
    ...createUppdragV2(),
    onSuccess: async (uppdrag: Uppdrag) => {
      queryClient.invalidateQueries({ queryKey: [UppdragQueryKey.GetList] });
      queryClient.invalidateQueries({ queryKey: [UppdragQueryKey.GetOne] });
      queryClient.invalidateQueries({
        queryKey: [TransportSedelQueryKey.GetList],
      });

      notification.success("Uppdrag skapat!");
      closeModal();

      // Different navigation based on user role
      if (isOperator) {
        // For operators, navigate to specific inspection if created
        const fiveYearsInspection = uppdrag.fiveYearsInspectionList.find(
          () => true,
        );
        const sixMonthsInspection = uppdrag.sixMonthsInspectionList.find(
          () => true,
        );
        const waterMeasurementProtocol =
          uppdrag.waterMeasurementProtocolList.find(() => true);

        if (fiveYearsInspection) {
          navigate({
            to: "/app/uppdrag/$uuid/fivey/$fiveyid",
            params: {
              uuid: uppdrag.uuid,
              fiveyid: fiveYearsInspection.id.toString(),
            },
          });
        } else if (sixMonthsInspection) {
          navigate({
            to: "/app/uppdrag/$uuid/sixm/$sixmid",
            params: {
              uuid: uppdrag.uuid,
              sixmid: sixMonthsInspection.id.toString(),
            },
          });
        } else if (waterMeasurementProtocol) {
          navigate({
            to: "/app/uppdrag/$uuid/provtagning/$pid",
            params: {
              uuid: uppdrag.uuid,
              pid: waterMeasurementProtocol.id.toString(),
            },
          });
        } else if (uppdrag.transportSedelList.length > 0) {
          navigate({
            to: "/app/uppdrag/$uuid",
            params: { uuid: uppdrag.uuid },
            search: { tab: UppdragPageTab.TransportSedel },
          });
        } else {
          navigate({
            to: "/app/uppdrag/$uuid",
            params: { uuid: uppdrag.uuid },
          });
        }
      } else {
        // For admins, navigate to uppdrag page
        navigate({
          to: "/app/uppdrag/$uuid",
          params: { uuid: uppdrag.uuid },
        });
      }
    },
    onError: () => {
      notification.error("Kunde inte skapa uppdrag");
    },
  });

  const onSubmit = (data: FormData) => {
    let wasteStationDeliveryDate: Date | null | undefined;
    // Check if selected waste station requires delivery date notification
    if (data.wasteStationUuid && wasteStations) {
      const selectedStation = wasteStations.find(
        (ws) => ws.uuid === data.wasteStationUuid,
      );
      if (
        isWasteStationNotificationEnabled &&
        selectedStation?.wantsToBeNotified
      ) {
        let hasError = false;
        if (!data.wasteStationDeliveryDate) {
          form.setError("wasteStationDeliveryDate", {
            type: "manual",
            message: "Leveransdatum är obligatoriskt för vald mottagarstation",
          });
          hasError = true;
        }
        if (!data.preliminaryWeight) {
          form.setError("preliminaryWeight", {
            type: "manual",
            message: "Uppskattad vikt är obligatorisk för vald mottagarstation",
          });
          hasError = true;
        }
        if (hasError) {
          return;
        }
      }
      // Only send delivery date if the station wants to be notified,
      // otherwise the server creates an unwanted waste station notification
      if (
        isWasteStationNotificationEnabled &&
        selectedStation?.wantsToBeNotified
      ) {
        wasteStationDeliveryDate = data.wasteStationDeliveryDate;
      }
    }

    // Use single endpoint with appropriate data based on role
    createUppdragMutation.mutate({
      customerUuid: data.customerUuid,
      facilityUuid: data.facilityUuid,
      assignedToUserUuid: isOperator
        ? user?.uuid || ""
        : data.assignedToUserUuid || "",
      scheduleDate: isOperator ? new Date() : data.scheduleDate || new Date(),
      scheduleDateType: isOperator
        ? UppdragScheduleDateType.Day
        : ((data.scheduleDateType || UppdragScheduleDateType.Day) as string),
      isPrioritized: isOperator ? false : data.isPrioritized || false,
      description: isOperator ? undefined : data.description,
      referenceNumber: isOperator ? undefined : data.referenceNumber,
      notes: isOperator ? "" : undefined,
      estimatedWorkingHours: data.estimatedWorkingHours,
      estimatedDrivingHours: data.estimatedDrivingHours,
      separatorUuid: data.separatorUuid === null ? "" : data.separatorUuid,
      uppgifter: data.uppgifter,
      wasteCodeUuid: data.wasteCodeUuid,
      wasteStationUuid: data.wasteStationUuid,
      preliminaryWeight: data.preliminaryWeight,
      tomningType: data.tomningType,
      status: isOperator ? UppdragStatus.Started : UppdragStatus.Scheduled,
      wasteStationDeliveryDate,
    });
  };

  const scheduleDateType = form.watch("scheduleDateType");
  const scheduleDate = form.watch("scheduleDate");

  const wasteStationUuid = form.watch("wasteStationUuid");

  const selectedWasteStation = useMemo(
    () => (wasteStations ?? []).find((w) => w.uuid === wasteStationUuid),
    [wasteStationUuid, wasteStations],
  );

  return {
    form,
    isModalOpen,
    openModal,
    closeModal,
    afterModalClose,
    mutation: createUppdragMutation,
    onSubmit: form.handleSubmit(onSubmit),
    facilitySeparatorList,
    getSeparatorDropdownItems,
    userDropdownItems,
    isAvskiljare,
    isWaterMeasurementPoint,
    wasteCodeComboBoxItems,
    setWasteCodeQuery,
    selectedWasteCode,
    setSelectedWasteCode,
    mostUsedWasteCodes,
    selectMostUsedWasteCode,
    wasteStationDropdownItems,
    company,
    scheduleDateType,
    scheduleDate,
    isCustomerProvided: true,
    isFacilityProvided: true,
    isOperator,
    user,
    facility,
    selectedWasteStation,
  };
};

export type UseAddUppdragFromFacilityPageReturnType = ReturnType<
  typeof useAddUppdragFromFacilityPage
>;
